import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

// Styles
import * as S from './styles';

// Components
import KogModalFadeComponent from '@/main/components/modal/ModalFadeComponent';
import SecondaryButton from '@/main/components/shared/button/secondaryButton';
import Button from '@/main/components/shared/button/button';

// Utils
import {
  getTranslateByKey,
  isNullUndefined,
  isNullUndefinedEmpty
} from '@/main/services/utils';
import { RoutesConstants } from '@/main/routes/routesConstants';
import { accountApi } from '@/main/api';

// Redux
import {
  useUserReducer,
  userOpenModalUserChangePasswordAlert
} from '@/main/store/user';

interface IModalUserChangePasswordAlertProps {
  isOpenModal: boolean;
  setIsOpenModal: React.Dispatch<React.SetStateAction<boolean>>;
  closeModal: () => void;
}

function ModalUserChangePasswordAlert({
  closeModal
}: IModalUserChangePasswordAlertProps) {
  const navigate = useNavigate();
  const location = useLocation();

  // States
  const [isCallingApi, setIsCallingApi] = useState<boolean>(false);

  // Constants
  const PREFIX_ID_ELEMENTS_MODAL = 'id-modalUserChangePasswordAlert-';

  // Reducers
  const userReducer = useUserReducer();

  // Functions
  function getName() {
    if (
      !isNullUndefined(userReducer.currentUser) &&
      !isNullUndefinedEmpty(userReducer.currentUser.name)
    ) {
      return userReducer.currentUser.name;
    }
    return '';
  }

  function getUserName() {
    if (
      !isNullUndefined(userReducer.currentUser) &&
      !isNullUndefinedEmpty(userReducer.currentUser.userName)
    ) {
      return userReducer.currentUser.userName;
    }
    return '';
  }

  function getNumberOfDaysToPasswordExpires() {
    if (!isNullUndefined(userReducer.numberOfDaysToPasswordExpires)) {
      return userReducer.numberOfDaysToPasswordExpires;
    }

    return 0;
  }

  return (
    <S.ContainerModal>
      <S.ContainerContentView>
        <span>
          {`${getTranslateByKey('information.dearUserName').replace(
            '{0}',
            getName()
          )},`}
        </span>
        <span>{getTranslateByKey('information.changePasswordMessage1')}</span>
        <span>
          {getTranslateByKey('information.currentPwdExpiresInXdays').replace(
            '{0}',
            String(getNumberOfDaysToPasswordExpires())
          )}
        </span>
      </S.ContainerContentView>
      <S.AreaButtons>
        <SecondaryButton
          id={`${PREFIX_ID_ELEMENTS_MODAL}btn-change-password-later`}
          type="button"
          isLoading={false}
          onClick={() => {
            closeModal();
          }}>
          {getTranslateByKey('information.changePasswordLater')}
        </SecondaryButton>
        <Button
          type="button"
          isLoading={isCallingApi}
          onClick={() => {
            setIsCallingApi(true);
            accountApi
              .getUserToken(getUserName())
              .then(response => {
                if (
                  !isNullUndefined(response.data.result) &&
                  !isNullUndefinedEmpty(response.data.result.token) &&
                  !isNullUndefinedEmpty(response.data.result.id)
                ) {
                  const { id, token } = response.data.result;
                  navigate(
                    `${RoutesConstants.accountResetPassword}?token=${token}&userId=${id}`,
                    { state: { redirectRoute: location.pathname } }
                  );
                }
              })
              .finally(() => setIsCallingApi(false));
          }}
          style={{ marginLeft: 'unset' }}
          id={`${PREFIX_ID_ELEMENTS_MODAL}btn-change-password-now`}>
          {getTranslateByKey('information.changePasswordNow')}
        </Button>
      </S.AreaButtons>
    </S.ContainerModal>
  );
}

function WrapperModalUserChangePasswordAlert() {
  const dispatch = useDispatch();
  // Reducers
  const { openModalUserChangePasswordAlert } = useUserReducer();

  // States
  const [
    isOpenModalUserChangePasswordAlert,
    setIsOpenModalUserChangePasswordAlert
  ] = useState<boolean>(false);

  const props = {
    isOpenModal: isOpenModalUserChangePasswordAlert,
    setIsOpenModal: setIsOpenModalUserChangePasswordAlert,
    closeModal: () => setIsOpenModalUserChangePasswordAlert(false)
  };

  useEffect(() => {
    if (openModalUserChangePasswordAlert) {
      setIsOpenModalUserChangePasswordAlert(true);
      dispatch(userOpenModalUserChangePasswordAlert(false));
    }
  }, [openModalUserChangePasswordAlert]);

  return (
    <KogModalFadeComponent
      open={isOpenModalUserChangePasswordAlert}
      setOpen={setIsOpenModalUserChangePasswordAlert}>
      <ModalUserChangePasswordAlert {...props} />
    </KogModalFadeComponent>
  );
}

export default WrapperModalUserChangePasswordAlert;
